import {
  Chart,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale,
  LineController,
  Tooltip,
  Filler,
} from "chart.js";
import Papa from "papaparse";

Chart.register(
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale,
  LineController,
  Tooltip,
  Filler,
);

// Maximum date to include in reports (YYYY-MM-DD format)
const MAX_DATE = "2025-11-10";

interface RowData {
  date: string;
  dealer: string;
  rc_number: string;
  territory: string;
  qr_scans: number;
  store_visits: number;
  phone_calls: number;
  get_directions: number;
  maestro_visits: number;
  dealer_website_clicks: number;
}

interface MetricDataPoint {
  date: Date;
  value: number;
}

interface TrendData {
  percent: number;
  current: number;
  previous: number;
}

class DashboardApp {
  private data: RowData[] = [];
  private currentDealer: string | null = null;
  private charts: Record<string, Chart<"line">> = {};
  private originalDealers: string[] = [];

  private getDealerByRc(rc: string): string | null {
    const match = this.data.find(
      (r) => r.rc_number.toUpperCase() === rc.toUpperCase(),
    );
    return match ? match.dealer : null;
  }

  private getRcForDealer(dealer: string): string | null {
    const match = this.data.find((r) => r.dealer === dealer);
    return match ? match.rc_number : null;
  }

  private setUrlRc(rc: string | null) {
    const url = new URL(window.location.href);
    if (rc) {
      url.searchParams.set("rc", rc);
    } else {
      url.searchParams.delete("rc");
    }
    window.history.replaceState({}, "", url.toString());
  }

  constructor() {
    this.init();
  }

  clearSearch() {
    const search = document.getElementById(
      "dealerSearch",
    ) as HTMLInputElement | null;
    if (!search) return;
    search.value = "";
  }
  async init() {
    await this.loadData();
    this.originalDealers = this.getUniqueValues("dealer");
    this.setupDealerSelect();
    this.setupDateRange();
    this.setupCharts();
    const clearDealerSearch = document.getElementById(
      "clearDealerSearch",
    ) as HTMLDivElement | null;

    if (clearDealerSearch)
      clearDealerSearch.addEventListener("click", () => this.clearSearch());
    const params = new URLSearchParams(window.location.search);
    const rcParam = params.get("rc");
    let initialDealer: string | null = null;
    if (rcParam) {
      initialDealer = this.getDealerByRc(rcParam);
    }
    if (!initialDealer) {
      initialDealer = this.originalDealers[0] || null;
    }
    if (initialDealer) {
      this.selectDealer(initialDealer);
    }
  }

  async loadData() {
    try {
      // const response = await fetch("/data/output.csv");

      const response = await fetch("tranformed_data/updated_output.csv");

      const csvText = await response.text();

      const parsed = Papa.parse<RowData>(csvText, {
        header: true,
        skipEmptyLines: true,
        transform: (value: string, field: string | number) => {
          if (field === "date") return value;
          if (
            field === "dealer" ||
            field === "rc_number" ||
            field === "territory"
          )
            return value;
          return parseFloat(value) || 0;
        },
      });

      this.data = (parsed.data as RowData[])
        .filter((row) => row.date <= MAX_DATE)
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
      console.log("Data loaded:", this.data.length, "rows");
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }

  getUniqueValues(field: keyof RowData): string[] {
    return [...new Set(this.data.map((row) => row[field]))].filter(
      Boolean,
    ) as string[];
  }

  setupDealerSelect() {
    const select = document.getElementById("dealerSelect") as HTMLSelectElement;
    const search = document.getElementById(
      "dealerSearch",
    ) as HTMLInputElement | null;
    const optionsEl = document.getElementById(
      "dealerOptions",
    ) as HTMLUListElement | null;
    if (!select) return;

    const populateSelect = (dealers: string[]) => {
      const current = this.currentDealer;
      select.innerHTML = '<option value="">Select a dealer...</option>';
      dealers.forEach((dealer) => {
        const option = document.createElement("option");
        option.value = dealer;
        option.textContent = dealer;
        select.appendChild(option);
      });
      if (current && dealers.includes(current)) {
        select.value = current;
      }
    };

    const populateOptions = (dealers: string[]) => {
      if (!optionsEl) return;
      optionsEl.innerHTML = "";
      dealers.slice(0, 50).forEach((dealer, idx) => {
        const li = document.createElement("li");
        li.textContent = dealer;
        li.setAttribute("role", "option");
        li.addEventListener("mousedown", (e) => {
          e.preventDefault();
          this.selectDealer(dealer);
          if (search) search.value = dealer;
          optionsEl.classList.remove("show");
        });
        optionsEl.appendChild(li);
        if (idx === 0) li.classList.add("active");
      });
      if (dealers.length > 0 && optionsEl) optionsEl.classList.add("show");
      if (dealers.length === 0 && optionsEl) optionsEl.classList.remove("show");
    };

    populateSelect(this.originalDealers);

    select.addEventListener("change", (e) => {
      const target = e.target as HTMLSelectElement;
      if (target.value) {
        this.selectDealer(target.value);
        if (search) search.value = target.value;
      }
    });

    if (search) {
      search.addEventListener("click", () => {
        if (search.value != "") {
          this.clearSearch();

          // if (!optionsEl) return;
          // const items = Array.from(optionsEl.querySelectorAll("li"));
          // items.forEach((el) => el.classList.add("active"));

          populateOptions(this.originalDealers);
        }
      });
      search.addEventListener("focusout", () => {
        if (search.value == "") {
          if (this.currentDealer) {
            search.value = this.currentDealer;
          }
        }
      });
      search.addEventListener("input", () => {
        const q = search.value.toLowerCase();
        const filtered = q
          ? this.originalDealers.filter((d) => d.toLowerCase().includes(q))
          : this.originalDealers;
        populateOptions(filtered);
      });

      // Keyboard navigation for combobox
      search.addEventListener("keydown", (e) => {
        if (!optionsEl) return;
        const items = Array.from(optionsEl.querySelectorAll("li"));
        if (items.length === 0) return;
        const currentIdx = items.findIndex((el) =>
          el.classList.contains("active"),
        );
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const next = currentIdx < items.length - 1 ? currentIdx + 1 : 0;
          items.forEach((el, i) => el.classList.toggle("active", i === next));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          const prev = currentIdx > 0 ? currentIdx - 1 : items.length - 1;
          items.forEach((el, i) => el.classList.toggle("active", i === prev));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const active = items[currentIdx >= 0 ? currentIdx : 0];
          if (active) {
            const dealer = active.textContent || "";
            this.selectDealer(dealer);
            search.value = dealer;
            optionsEl.classList.remove("show");
          }
        } else if (e.key === "Escape") {
          optionsEl.classList.remove("show");
        }
      });

      search.addEventListener("focus", () => {
        const q = search.value.toLowerCase();
        const filtered = q
          ? this.originalDealers.filter((d) => d.toLowerCase().includes(q))
          : this.originalDealers;
        populateOptions(filtered);
      });

      document.addEventListener("click", (evt) => {
        if (!optionsEl || !search) return;
        if (!optionsEl.contains(evt.target as Node) && evt.target !== search) {
          optionsEl.classList.remove("show");
        }
      });
    }
  }

  setupDateRange() {
    const startDateInput = document.getElementById(
      "startDate",
    ) as HTMLInputElement;
    const endDateInput = document.getElementById("endDate") as HTMLInputElement;
    if (!startDateInput || !endDateInput) return;

    const dates = this.data
      .map((row) => new Date(row.date))
      .sort((a, b) => a.getTime() - b.getTime());
    const minDate = dates[0];
    const maxDate = dates[dates.length - 1];
    if (!minDate || !maxDate) return;

    startDateInput.value = minDate.toISOString().split("T")[0];
    endDateInput.value = maxDate.toISOString().split("T")[0];
    startDateInput.min = minDate.toISOString().split("T")[0];
    startDateInput.max = maxDate.toISOString().split("T")[0];
    endDateInput.min = minDate.toISOString().split("T")[0];
    endDateInput.max = maxDate.toISOString().split("T")[0];

    startDateInput.addEventListener("change", () => this.handleDateChange());
    endDateInput.addEventListener("change", () => this.handleDateChange());
  }

  handleDateChange() {
    if (this.currentDealer) {
      this.updateMetrics();
      this.updateCharts();
    }
  }

  selectDealer(dealerName: string) {
    this.currentDealer = dealerName;
    const select = document.getElementById("dealerSelect") as HTMLSelectElement;
    const title = document.getElementById("dealerName") as HTMLSpanElement;
    const dealerSearch = document.getElementById(
      "dealerSearch",
    ) as HTMLInputElement | null;
    if (select) {
      select.value = dealerName;
    }
    if (dealerSearch) {
      if (dealerSearch.value == "") {
        dealerSearch.value = dealerName;
      }
    }
    // Sync URL rc param for deep-linking
    const rc = this.getRcForDealer(dealerName);
    this.setUrlRc(rc);

    this.updateMetrics();
    this.updateCharts();
    title.innerText = dealerName;
    document.title = `${dealerName} : Mirage Dealer KPI Dashboard`;
  }

  getFilteredData(): RowData[] {
    const startDateInput = document.getElementById(
      "startDate",
    ) as HTMLInputElement;
    const endDateInput = document.getElementById("endDate") as HTMLInputElement;
    if (!startDateInput || !endDateInput) return this.data;

    const startDate = startDateInput.value;
    const endDate = endDateInput.value;

    return this.data.filter((row) => {
      const rowDate = row.date;
      return rowDate >= startDate && rowDate <= endDate;
    });
  }

  getMetricData(dealer: string, metric: keyof RowData): MetricDataPoint[] {
    return this.getFilteredData()
      .filter((row) => row.dealer === dealer)
      .map((row) => ({
        date: new Date(row.date),
        value: (row[metric] as number) || 0,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  getMetricDataForChart(
    dealer: string,
    metric: keyof RowData,
  ): MetricDataPoint[] {
    const dealerData = this.getMetricData(dealer, metric);

    if (dealerData.length === 0) return [];

    // Create a map using the original row dates (strings) for accurate matching
    const dataMapByDate = new Map<string, number>();
    this.getFilteredData()
      .filter((row) => row.dealer === dealer)
      .forEach((row) => {
        dataMapByDate.set(row.date, (row[metric] as number) || 0);
      });

    // Get date range
    const dates = Array.from(dataMapByDate.keys()).sort();
    const startDateStr = dates[0];
    const endDateStr = dates[dates.length - 1];

    // Fill in all days between start and end using EST timezone
    const filledData: MetricDataPoint[] = [];
    const currentDate = new Date(startDateStr + "T12:00:00-05:00"); // Noon EST to avoid timezone issues
    const endDate = new Date(endDateStr + "T12:00:00-05:00");

    while (currentDate <= endDate) {
      // Format date as YYYY-MM-DD in EST
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      filledData.push({
        date: new Date(currentDate),
        value: dataMapByDate.get(dateStr) || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return filledData;
  }

  calculateTrend(data: MetricDataPoint[], days = 7): TrendData {
    if (data.length < days) return { percent: 0, current: 0, previous: 0 };

    const recent = data.slice(-days);
    const previous = data.slice(-days * 2, -days);

    if (previous.length === 0) return { percent: 0, current: 0, previous: 0 };

    const recentAvg =
      recent.reduce((sum, item) => sum + item.value, 0) / recent.length;
    const previousAvg =
      previous.reduce((sum, item) => sum + item.value, 0) / previous.length;

    const percent =
      previousAvg === 0
        ? recentAvg > 0
          ? 100
          : 0
        : ((recentAvg - previousAvg) / previousAvg) * 100;

    return {
      percent: percent,
      current: Math.round(recentAvg),
      previous: Math.round(previousAvg),
    };
  }

  updateMetrics() {
    if (!this.currentDealer) return;

    const metrics = [
      { key: "qr_scans", elementId: "qr-scans" },
      { key: "store_visits", elementId: "store-visits" },
      { key: "phone_calls", elementId: "phone-calls" },
      { key: "get_directions", elementId: "get-directions" },
      { key: "maestro_visits", elementId: "maestro-visits" },
      { key: "dealer_website_clicks", elementId: "dealer-website-clicks" },
    ];

    metrics.forEach(({ key, elementId }) => {
      const data = this.getMetricData(this.currentDealer, key);
      if (data.length === 0) return;

      const total = data.reduce((sum, item) => sum + item.value, 0);
      const trendData = this.calculateTrend(data);

      const valueEl = document.getElementById(`${elementId}-value`);
      const comparisonEl = document.getElementById(`${elementId}-comparison`);

      if (valueEl) {
        valueEl.textContent = Math.round(total).toLocaleString();
      }

      if (comparisonEl) {
        const trendText =
          trendData.percent > 0
            ? `+${trendData.percent.toFixed(1)}%`
            : `${trendData.percent.toFixed(1)}%`;
        const trendClass = trendData.percent > 0 ? "positive" : "negative";

        comparisonEl.innerHTML = `
                    <span class="comparison-values">
                        <span class="current-value">${trendData.current.toLocaleString()}</span>
                        <span class="vs-text">vs</span>
                        <span class="previous-value">${trendData.previous.toLocaleString()}</span>
                    </span>
                    <span class="comparison-percent ${trendClass}">${trendText}</span>
                `;
      }
    });
  }

  setupCharts() {
    const chartConfigs = [
      { canvasId: "qr-scans-chart", metric: "qr_scans" },
      { canvasId: "store-visits-chart", metric: "store_visits" },
      { canvasId: "phone-calls-chart", metric: "phone_calls" },
      { canvasId: "get-directions-chart", metric: "get_directions" },
      { canvasId: "maestro-visits-chart", metric: "maestro_visits" },
      {
        canvasId: "dealer-website-clicks-chart",
        metric: "dealer_website_clicks",
      },
    ];

    chartConfigs.forEach(({ canvasId, metric }) => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const isPrimaryCard = canvasId === "qr-scans-chart";

      this.charts[metric] = new Chart(ctx, {
        type: "line",
        data: {
          labels: [],
          datasets: [
            {
              data: [],
              borderColor: isPrimaryCard ? "#68d391" : "#4299e1",
              backgroundColor: isPrimaryCard
                ? "rgba(104, 211, 145, 0.1)"
                : "rgba(66, 153, 225, 0.1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: (items) => {
                  if (items[0]) {
                    const date = new Date(items[0].label);
                    return date.toLocaleDateString();
                  }
                  return "";
                },
                label: (item) =>
                  `${Math.round(item.parsed.y || 0)} ${metric.replace("_", " ")}`,
              },
            },
          },
          scales: {
            x: {
              display: false,
              grid: { display: false },
            },
            y: {
              display: true,
              grid: { display: true, color: "rgba(0, 0, 0, 0.05)" },
              ticks: {
                font: { size: 10 },
                color: isPrimaryCard ? "#e2e8f0" : "#718096",
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
        },
      });
    });
  }

  updateCharts() {
    if (!this.currentDealer) return;

    Object.entries(this.charts).forEach(([metric, chart]) => {
      if (!this.currentDealer) return;
      const data = this.getMetricDataForChart(this.currentDealer, metric);

      chart.data.labels = data.map((d) => d.date.toISOString());
      chart.data.datasets[0].data = data.map((d) => d.value);
      chart.update("none");
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new DashboardApp();
});
